import { useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ColumnDropPosition } from '@/components/workspace/useColumnOrder';

/** Full-height insert marker drawn at the active drop gap. */
export function WorkspaceColumnInsertIndicator({
  activeDragColumnId,
  hoverColumnId,
  hoverPosition,
}: {
  activeDragColumnId?: string | null;
  hoverColumnId?: string | null;
  hoverPosition?: ColumnDropPosition | null;
}) {
  const [line, setLine] = useState<{
    left: number;
    top: number;
    height: number;
  } | null>(null);

  useLayoutEffect(() => {
    if (!activeDragColumnId || !hoverColumnId || !hoverPosition) {
      setLine(null);
      return;
    }

    const update = () => {
      const th = document.querySelector(
        `th[data-workspace-col-id="${CSS.escape(hoverColumnId)}"]`
      ) as HTMLElement | null;
      const table = th?.closest('table');
      if (!th || !table) {
        setLine(null);
        return;
      }

      const thRect = th.getBoundingClientRect();
      const tableRect = table.getBoundingClientRect();
      setLine({
        left: hoverPosition === 'after' ? thRect.right - 1.5 : thRect.left - 1.5,
        top: tableRect.top,
        height: tableRect.height,
      });
    };

    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [activeDragColumnId, hoverColumnId, hoverPosition]);

  if (!line || typeof document === 'undefined') return null;

  return createPortal(
    <div
      data-workspace-insert-indicator="true"
      aria-hidden
      style={{
        position: 'fixed',
        left: `${line.left}px`,
        top: `${line.top}px`,
        width: '3px',
        height: `${line.height}px`,
        background: 'var(--brand-primary)',
        boxShadow: '0 0 0 1px rgba(255,255,255,0.9)',
        borderRadius: '2px',
        zIndex: 10000,
        pointerEvents: 'none',
      }}
    />,
    document.body
  );
}
